import * as React from 'react'
import { type SelectChangeEvent } from '@mui/material/Select'
import { useNavigate } from 'react-router-dom'
import { type SnackbarOrigin } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import EnhancedTable from './EmailNotificationTable'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import LanguageStrings from '../../../i18n/locales'
import { Button } from '../../../components/Button'
import { useAuth } from 'react-oidc-context'
import { type Sort } from './EmailNotificationTable'
import { type OperationPermissionState } from '../../dashboard/redux/operationPermissionSlice'
import { getOperationPermissions } from '../../dashboard/redux/actionCreator'
import EmailAlertModal from './EmailAlertModal'
import { type EmailGroupsListState } from '../redux/emailGroupsListSlice'
import { fetchEmailGroups } from '../redux/actionCreators'
import { Link } from '@mui/material'

export interface State extends SnackbarOrigin {
  open: boolean
}
const listStrings = LanguageStrings().ThermalIQStrings

function EnhancedTableToolbar (props: any): JSX.Element {
  const { numSelected } = props
  return (

        <div>
            {numSelected > 0
              ? (
                <Typography
                    sx={{ flex: '1 1 100%', paddingLeft: '16px', fontFamily: 'Honeywell Sans Web' }}
                    color='inherit'
                    variant='subtitle1'
                    component='div'
                >
                    {numSelected} {listStrings.EmailNotificationComponent.Selected}
                </Typography>
                )
              : (
                <div className='configurations-table-heading'>
                    <Typography color='inherit' sx={{ fontFamily: 'Honeywell Sans Web', fontSize: '16px', fontWeight: '700', lineHeight: '24px', letterSpacing: '2.5px' }}>{listStrings.EmailNotificationComponent.EmailAlertNotificationsList}</Typography>
                </div>
                )}
        </div>
  )
}

const style: React.CSSProperties = {
  width: '99%',
  backgroundColor: '#272727',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0
}

function EmailNotificationList (props: PropsFromRedux): JSX.Element {
  const DashboardStrings = LanguageStrings().Dashboard
  const [selected] = React.useState<readonly string[]>([])
  const [sortCondition, setSortCondition] = React.useState('')
  const rowsPerPage = listStrings.EmailNotificationComponent.RowPerPage
  const [page, setPage] = React.useState<number>(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'desc', orderBy: 'createdAt' })
  const [showCreateEmailAlert, setShowCreateEmailAlert] = React.useState<boolean>(false)
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()

  const navigate = useNavigate()

  const handleChange = (event: SelectChangeEvent): void => {
    setSortCondition(event.target.value)
  }

  function handleBreadCrumbsClick (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    event.preventDefault()
  }

  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  return (
        <div>
          <div role='presentation' data-testid='breadcrumbs' onClick={handleBreadCrumbsClick} className='AmiHeading' >
            <ArrowBackIos className="assetmgmtli" data-testid="breadcrumb-arrow-back" aria-label="navArrow" sx={{ marginTop: '10px' }}
              onClick={() => { navigate(-1) }}
            />
            <Breadcrumbs aria-label='breadcrumb' sx={{ color: '#f0f0f0', fontSize: '12px', marginBottom: '14px', fontFamily: 'Honeywell Sans Web', marginTop: '12px' }}>
                <Link aria-label='navBreadcrumb' className='assetmgmtli' underline='none' color='inherit' onClick={() => { navigate('/thermal-dashboard') }} >
                    {DashboardStrings?.HOME}
                </Link>
              <Typography color='inherit' sx={{ fontSize: '12px', fontFamily: 'Honeywell Sans Web' }}>{listStrings.EmailNotificationComponent.Services}</Typography>
            </Breadcrumbs>
                        </div>
              <div className='ArrowDeviceList'>
                  <div>
                    <span data-testid='deviceHistoryDetails' className='EmailListComponent'>{listStrings.EmailNotificationComponent.Services}</span>                  </div>
                  {
                    operationPermissions.permissions.can_add_tiq_communications_service
                      ? <Button sx={{ marginRight: '1em', padding: '1em', display: 'flex', alignItems: 'center' }} width='fit-content' onClick={() => { setShowCreateEmailAlert(true) }} dataTestId='create-email-alert-btn'>{listStrings.EmailNotificationComponent.CreateEmailAlert}</Button>
                      : <></>
                  }
              </div>
          <EnhancedTableToolbar numSelected={selected.length} handleChange={handleChange}
                          sortCondition={sortCondition}
                      />
          <div style={style}>
            <EnhancedTable page={page} rowsPerPage={rowsPerPage} sort={sort} setPage={setPage} setSort={setSort} orderBy={'EnableDisable'}/>
          </div>          {showCreateEmailAlert && (
            <EmailAlertModal
              open={showCreateEmailAlert}
              onClose={() => { setShowCreateEmailAlert(false) }}
              mode="create"
            />
          )}
        </div>
  )
}

interface DispatchToProps {
  getOperationPermissions: (sub: string) => void
  fetchEmailGroups: (
    page: number,
    size: number,
    sortEnabled: boolean
  ) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub)),
  fetchEmailGroups: (
    page: number,
    size: number,
    sortEnabled: boolean
  ) => dispatch(fetchEmailGroups(page, size, sortEnabled))
})

interface StateToProps {
  grandParentBreadcrumb: string
  emailGroupsList: EmailGroupsListState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  grandParentBreadcrumb: state.grandParentBreadcrumb,
  emailGroupsList: state.emailGroupsList
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(EmailNotificationList)
